import React, { useState } from 'react';
import { Search, X, Lock, Clock, ShoppingCart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '../../contexts/CartContext';
import useProductSearch from '../../hooks/useProductSearch';

const SearchBar = ({ mobile = false, onClose }) => {
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { query, setQuery, results, loading, isOpen, close, clear, dropdownRef } = useProductSearch();
  const [focused, setFocused] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.length >= 2) {
      close();
      navigate(`/search?q=${encodeURIComponent(query)}`);
      if (onClose) onClose();
    }
  };

  const handleProductClick = (productId) => {
    close();
    navigate(`/products/${productId}`);
    if (onClose) onClose();
  };

  const handleWholesalerClick = (wholesalerId) => {
    close();
    navigate(`/wholesalers/${wholesalerId}`);
    if (onClose) onClose();
  };

  const handleAddToCart = (e, product) => {
    e.stopPropagation();
    if (!product.pricing || !product.wholesaler) return;
    addToCart({
      _id: product._id,
      name: product.name,
      pricing: product.pricing,
      images: product.images,
      wholesalerId: product.wholesaler._id,
      wholesalerName: product.wholesaler.companyName,
      quantity: product.pricing.minOrderQuantity || 1
    });
  };

  const stockDot = (status) => {
    if (status === 'in_stock') return 'bg-green-500';
    if (status === 'low_stock') return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div ref={dropdownRef} className={`relative ${mobile ? 'w-full' : ''}`}>
      {/* Search Input */}
      <form onSubmit={handleSubmit} className="relative">
        <div className={`flex items-center bg-gray-100 rounded-lg transition-all ${
          mobile ? 'w-full' : focused ? 'w-80' : 'w-64'
        }`}>
          <Search className="w-4 h-4 text-gray-400 ml-3 flex-shrink-0" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder={t('common:productSearch.placeholder', 'Search products across wholesalers...')}
            className="w-full bg-transparent px-2 py-2 text-sm text-gray-900 placeholder-gray-500 focus:outline-none"
          />
          {query && (
            <button
              type="button"
              onClick={clear}
              className="p-1 mr-1 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </form>

      {/* Dropdown Results */}
      {isOpen && results && (
        <div className={`absolute top-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden ${
          mobile ? 'left-0 right-0' : 'left-0 w-[min(420px,calc(100vw-2rem))]'
        }`}>
          {/* Loading indicator */}
          {loading && (
            <div className="px-4 py-3 text-sm text-gray-500 flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              {t('common:productSearch.searching', 'Searching...')}
            </div>
          )}

          {/* Products */}
          {results.products?.length > 0 && (
            <div className="max-h-[360px] overflow-y-auto">
              {results.products.map((product) => (
                <div
                  key={product._id}
                  onClick={() => handleProductClick(product._id)}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-0"
                >
                  {/* Thumbnail */}
                  <div className="w-10 h-10 rounded-md bg-gray-100 flex-shrink-0 overflow-hidden">
                    {product.images?.[0] ? (
                      <img src={product.images[0]} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300">
                        <Search className="w-4 h-4" />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${stockDot(product.stockStatus)}`} />
                      <p className="text-sm font-medium text-gray-900 truncate">{product.name}</p>
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {product.wholesaler?.logo && (
                        <img src={product.wholesaler.logo} alt="" className="w-3.5 h-3.5 rounded-full" />
                      )}
                      <span className="text-xs text-gray-500 truncate">{product.wholesaler?.companyName}</span>
                    </div>
                  </div>

                  {/* Price + Cart */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-sm font-medium text-gray-900">
                      {product.pricing
                        ? `€${product.pricing.unitPrice?.toFixed(2)}/${product.pricing.unit}`
                        : <span className="text-xs text-gray-400 italic">{t('common:productSearch.priceOnAgreement', 'Price on agreement')}</span>
                      }
                    </span>
                    {product.pricing && (
                      <button
                        onClick={(e) => handleAddToCart(e, product)}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                        title={t('common:productSearch.addToCart', 'Add to cart')}
                      >
                        <ShoppingCart className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Private Catalog Teasers */}
          {results.privateTeasers?.length > 0 && (
            <>
              {results.products?.length > 0 && <div className="border-t border-gray-200" />}
              <div className="bg-gray-50">
                {results.privateTeasers.map((teaser) => (
                  <div
                    key={teaser.wholesaler._id}
                    onClick={() => handleWholesalerClick(teaser.wholesaler._id)}
                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-0"
                  >
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex-shrink-0 overflow-hidden flex items-center justify-center">
                      {teaser.wholesaler.logo ? (
                        <img src={teaser.wholesaler.logo} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Lock className="w-3.5 h-3.5 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-700 truncate">{teaser.wholesaler.companyName}</p>
                      <p className="text-xs text-gray-500">
                        {teaser.matchCount} {t('common:productSearch.productsMatch', 'products match')}
                      </p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      teaser.hasPendingRequest
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {teaser.hasPendingRequest
                        ? <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{t('common:productSearch.requestPending', 'Pending')}</span>
                        : t('common:productSearch.requestAccess', 'Request access')
                      }
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* No results */}
          {!loading && results.products?.length === 0 && results.privateTeasers?.length === 0 && (
            <div className="px-4 py-6 text-center text-sm text-gray-500">
              {t('common:productSearch.noResults', 'No products found')}
              <p className="text-xs text-gray-400 mt-1">{t('common:productSearch.noResultsHint', 'Try a different search term')}</p>
            </div>
          )}

          {/* View All Results link */}
          {results.count > 0 && (
            <div className="border-t border-gray-200">
              <button
                onClick={handleSubmit}
                className="w-full px-4 py-2.5 text-sm text-blue-600 hover:bg-blue-50 font-medium text-center"
              >
                {t('common:productSearch.viewAllResults', 'View all {{count}} results', { count: results.count })}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
